import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Tabs, Tray } from '@frontend/design-system';
import { ListsView } from '../lists-view/lists-view';
import { useListsView } from '../lists-view/use-lists-view';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { RecipientsView } from '../recipients-view/recipients-view';
import { SegmentsView } from '../segments-view/segments-view';
import { useSegmentsView } from '../segments-view/use-segments-view';
import { View } from '../types';

export const AudienceTabs = () => {
  const { t } = useTranslation('bulk-messaging');
  const { enableManualSelection, setMode, mode, selectedListIds, trackingIdPrefix, view } = useAudienceTray();
  const listsViewProps = useListsView();
  const segmentsViewProps = useSegmentsView();

  const getHeader = () => {
    switch (view) {
      case 'recipients':
        return (
          <RecipientsView.Header
            cancelCreateList={listsViewProps.cancelCreateList}
            cancelEditList={listsViewProps.cancelEditList}
          />
        );
      case 'segments':
        return <SegmentsView.Header />;
      case 'lists':
        return <ListsView.Header />;
      default:
        return null;
    }
  };

  const getFooter = () => {
    switch (view) {
      case 'recipients':
        return (
          <RecipientsView.Footer
            advanceCreateList={listsViewProps.advanceCreateList}
            cancelCreateList={listsViewProps.cancelCreateList}
            cancelEditList={listsViewProps.cancelEditList}
            finishEditList={listsViewProps.finishEditList}
          />
        );
      case 'segments':
        return (
          <SegmentsView.Footer
            duplicateRecipientsCount={segmentsViewProps.duplicateRecipientsCount}
            segmentsCount={segmentsViewProps.segments?.length ?? 0}
            segmentsRecipientsCount={segmentsViewProps.segmentsRecipientsCount}
            selectedListsCount={selectedListIds.length}
            selectedListsRecipientsCount={listsViewProps.selectedListsRecipientsCount}
          />
        );
      case 'lists':
        return (
          <ListsView.Footer
            duplicateRecipientsCount={segmentsViewProps.duplicateRecipientsCount}
            segmentsCount={segmentsViewProps.segments?.length ?? 0}
            segmentsRecipientsCount={segmentsViewProps.segmentsRecipientsCount}
            selectedListsCount={selectedListIds.length}
            selectedListsRecipientsCount={listsViewProps.selectedListsRecipientsCount}
          />
        );
      default:
        return null;
    }
  };

  // Disable tabs when the mode contains a sub mode such as creating or editing a list.
  // All of these sub modes manage their own navigation and the user should not be able to switch tabs.
  // The only exception is deleting a list. It looks kind of weird to disable the tabs in this case.
  const disableTabs = view !== mode && mode !== 'lists-delete-list';

  return (
    <>
      {getHeader()}
      <Tabs controlledTab={view} onChange={(id) => !disableTabs && setMode(id as View)}>
        <Tabs.Bar fullWidth css={{ margin: theme.spacing(2, 4, 0) }}>
          {enableManualSelection ? (
            <Tabs.Tab
              id='recipients'
              disabled={disableTabs && view !== 'recipients'}
              trackingId={`${trackingIdPrefix}-all-contacts-tab`}
            >
              {t('All Contacts')}
            </Tabs.Tab>
          ) : (
            <Tabs.Tab
              id='segments'
              disabled={disableTabs && view !== 'segments'}
              trackingId={`${trackingIdPrefix}-segments-tab`}
            >
              {t('Segments')}
            </Tabs.Tab>
          )}
          <Tabs.Tab
            id='lists'
            disabled={disableTabs && view !== 'lists'}
            trackingId={`${trackingIdPrefix}-saved-lists-tab`}
          >
            {t('Saved Lists')}
          </Tabs.Tab>
        </Tabs.Bar>
        <Tray.Body css={{ display: 'flex', flexDirection: 'column' }}>
          {enableManualSelection ? (
            <Tabs.Panel controller='recipients' css={{ height: '100%' }}>
              <RecipientsView startCreateListFrom={listsViewProps.startCreateListFrom} />
            </Tabs.Panel>
          ) : (
            <Tabs.Panel controller='segments' css={{ height: '100%' }}>
              <SegmentsView segments={segmentsViewProps.segments} />
            </Tabs.Panel>
          )}
          <Tabs.Panel controller='lists' css={{ height: '100%' }}>
            <ListsView {...listsViewProps} />
          </Tabs.Panel>
        </Tray.Body>
      </Tabs>
      {getFooter()}
    </>
  );
};
